import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Loader2,
  Scissors,
  AlertCircle,
  RotateCcw,
  Send,
  Check,
  ImageIcon,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Alert, AlertDescription } from "../../ui/alert";
import { DocumentCropper, DocumentCropperHandle } from "./DocumentCropper";
import { pdfToImage, isPdfFile } from "../../../utils/vault/pdfToImage";
import {
  fileToOrientedDataUrl,
  loadImageBitmapFromDataUrl,
} from "../../../utils/vault/loadImageWithOrientation";
import { createBasicDocumentCropCanvas } from "../../../utils/vault/documentCropFallback";
import type { CornerPoints } from "../../../types/jscanify";
import { cn } from "../../../lib/utils";
import { useBreakpoint } from "../../../hooks/useIsMobile";
import { useJscanify } from "../../../hooks/useJscanify";
import { useSingleFlight } from "../../../hooks/useSingleFlight";

interface Props {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  file: File | null;
  onCropComplete: (croppedFile: File) => void;
  onCancel: () => void;
  cropTitle?: string;
  cropDescription?: string;
  previewTitle?: string;
  previewDescription?: string;
}

// Maximum output dimension to prevent memory issues with very large selections
const MAX_OUTPUT_DIMENSION = 2000;

// Minimum output dimension to prevent OpenCV errors with tiny selections
const MIN_OUTPUT_DIMENSION = 10;

type CropStep = "crop" | "preview";

// Number of retry attempts before showing "use original" option
const MAX_RETRY_BEFORE_ORIGINAL = 1;

/**
 * Validates that corners are not crossed/inverted
 * Uses the shoelace formula to calculate the signed area of the quadrilateral
 * A negative area indicates crossed corners (clockwise vs counter-clockwise)
 */
const areCornersValid = (corners: CornerPoints): boolean => {
  const { topLeftCorner, topRightCorner, bottomRightCorner, bottomLeftCorner } =
    corners;

  // Check basic ordering: top corners should be above bottom corners
  // and left corners should be to the left of right corners
  const topAboveBottom =
    topLeftCorner.y < bottomLeftCorner.y &&
    topRightCorner.y < bottomRightCorner.y;

  const leftBeforeRight =
    topLeftCorner.x < topRightCorner.x &&
    bottomLeftCorner.x < bottomRightCorner.x;

  if (!topAboveBottom || !leftBeforeRight) {
    return false;
  }

  // Calculate signed area using shoelace formula
  // Positive area = counter-clockwise (correct), Negative = clockwise (crossed)
  const points = [
    topLeftCorner,
    topRightCorner,
    bottomRightCorner,
    bottomLeftCorner,
  ];
  let signedArea = 0;
  for (let i = 0; i < 4; i++) {
    const j = (i + 1) % 4;
    signedArea += points[i].x * points[j].y;
    signedArea -= points[j].x * points[i].y;
  }

  // Area should be positive for valid corner ordering
  return signedArea > 0;
};

/**
 * Calculate output dimensions based on the selected corner points
 * Uses maximum widths/heights to ensure no content is clipped during perspective transform
 * Outputs at full resolution (capped at MAX_OUTPUT_DIMENSION) for best quality
 */
const calculateOutputDimensions = (
  corners: CornerPoints,
): { width: number; height: number } => {
  // Calculate the width and height from corner points using Euclidean distance
  const topWidth = Math.sqrt(
    Math.pow(corners.topRightCorner.x - corners.topLeftCorner.x, 2) +
      Math.pow(corners.topRightCorner.y - corners.topLeftCorner.y, 2),
  );
  const bottomWidth = Math.sqrt(
    Math.pow(corners.bottomRightCorner.x - corners.bottomLeftCorner.x, 2) +
      Math.pow(corners.bottomRightCorner.y - corners.bottomLeftCorner.y, 2),
  );
  const leftHeight = Math.sqrt(
    Math.pow(corners.bottomLeftCorner.x - corners.topLeftCorner.x, 2) +
      Math.pow(corners.bottomLeftCorner.y - corners.topLeftCorner.y, 2),
  );
  const rightHeight = Math.sqrt(
    Math.pow(corners.bottomRightCorner.x - corners.topRightCorner.x, 2) +
      Math.pow(corners.bottomRightCorner.y - corners.topRightCorner.y, 2),
  );

  // Use MAXIMUM dimensions to ensure no content is lost during perspective transform
  // This is critical for nearly-rectangular selections where averaging can clip edges
  let width = Math.ceil(Math.max(topWidth, bottomWidth));
  let height = Math.ceil(Math.max(leftHeight, rightHeight));

  // Ensure minimum dimensions to prevent OpenCV errors
  width = Math.max(MIN_OUTPUT_DIMENSION, width);
  height = Math.max(MIN_OUTPUT_DIMENSION, height);

  // Scale down if exceeds max dimension (preserving aspect ratio)
  const maxDim = Math.max(width, height);
  if (maxDim > MAX_OUTPUT_DIMENSION) {
    const scale = MAX_OUTPUT_DIMENSION / maxDim;
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }

  return { width, height };
};

/**
 * Modal wrapper for the DocumentCropper component
 * Handles PDF conversion and file output with proper memory cleanup
 */
export const DocumentCropperModal: React.FC<Props> = ({
  isOpen,
  onOpenChange,
  file,
  onCropComplete,
  onCancel,
  cropTitle = "Ajusta el documento",
  cropDescription = "Arrastra las esquinas azules para ajustar el área de recorte del documento",
  previewTitle = "Confirma el recorte",
  previewDescription = "Revisa que la imagen recortada sea correcta antes de enviar",
}) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [isLoadingFile, setIsLoadingFile] = useState(false);
  const [corners, setCorners] = useState<CornerPoints | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<CropStep>("crop");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [croppedFileRef, setCroppedFileRef] = useState<File | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [cornersWarning, setCornersWarning] = useState<string | null>(null);

  const cropperRef = useRef<DocumentCropperHandle>(null);
  const { isMobile } = useBreakpoint();
  const {
    isLoading: scannerLoading,
    error: scannerError,
    retry: retryScanner,
  } = useJscanify();
  const { isPending: isProcessing, run: runCropAction } = useSingleFlight();
  const { isPending: isFinalizing, run: runFinalizeAction } = useSingleFlight();

  // Memoize the corners change handler to prevent unnecessary re-renders
  const handleCornersChange = useCallback((newCorners: CornerPoints) => {
    setCorners(newCorners);
  }, []);

  // Clean up preview URL on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Load file when modal opens with proper cleanup
  useEffect(() => {
    if (!isOpen || !file) {
      // Reset state when modal closes (previewUrl cleanup is handled by the previous effect)
      setImageSrc(null);
      setCorners(null);
      setError(null);
      setStep("crop");
      setPreviewUrl(null);
      setCroppedFileRef(null);
      setRetryCount(0);
      setCornersWarning(null);
      return;
    }

    let isCancelled = false;

    const loadFile = async () => {
      setIsLoadingFile(true);
      setError(null);

      try {
        if (isPdfFile(file)) {
          // Convert PDF to image
          const dataUrl = await pdfToImage(file, 2);
          if (!isCancelled) {
            setImageSrc(dataUrl);
          }
        } else {
          // Load image file with EXIF orientation applied
          // This uses createImageBitmap to ensure consistent orientation
          // across all browsers (fixes flipped/mirrored images from phone cameras)
          const dataUrl = await fileToOrientedDataUrl(file);

          if (!isCancelled) {
            setImageSrc(dataUrl);
          }
        }
      } catch (e) {
        if (!isCancelled) {
          setError(
            e instanceof Error
              ? e.message
              : "Error al procesar el archivo. Intenta con otro formato.",
          );
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingFile(false);
        }
      }
    };

    loadFile();

    return () => {
      isCancelled = true;
    };
  }, [isOpen, file]);

  // Handle crop confirmation - generates preview
  const handleConfirmCrop = async () => {
    if (!imageSrc || !corners) return;

    // Validate corners before processing
    if (!areCornersValid(corners)) {
      setCornersWarning(
        "Las esquinas parecen estar cruzadas. Ajusta la selección para que las esquinas superiores estén arriba de las inferiores.",
      );
      return;
    }

    setCornersWarning(null);
    setError(null); // Clear previous errors on retry
    await runCropAction(async () => {
      let imageBitmap: ImageBitmap | null = null;
      let canvas: HTMLCanvasElement | null = null;
      let resultCanvas: HTMLCanvasElement | null = null;

      try {
        // Load image with EXIF orientation applied using createImageBitmap
        // This ensures consistent orientation across all browsers
        imageBitmap = await loadImageBitmapFromDataUrl(imageSrc);

        // Create canvas with the properly oriented image
        canvas = document.createElement("canvas");
        canvas.width = imageBitmap.width;
        canvas.height = imageBitmap.height;
        const ctx = canvas.getContext("2d");

        if (!ctx) throw new Error("No se pudo crear el contexto del canvas");

        ctx.drawImage(imageBitmap, 0, 0);

        // Calculate dimensions based on the actual selection aspect ratio
        const dimensions = calculateOutputDimensions(corners);

        if (window.jscanify && window.cv?.imread) {
          try {
            const scanner = new window.jscanify();
            resultCanvas = scanner.extractPaper(
              canvas,
              dimensions.width,
              dimensions.height,
              corners,
            );
          } catch (scannerExtractionError) {
            if (import.meta.env.DEV) {
              console.warn(
                "Automatic document crop failed, using basic crop fallback:",
                scannerExtractionError,
              );
            }
          }
        }

        resultCanvas =
          resultCanvas ?? createBasicDocumentCropCanvas(canvas, corners);

        if (!resultCanvas) {
          throw new Error(
            "No se pudo generar un recorte válido. Ajusta las esquinas o usa la imagen original.",
          );
        }

        // Convert to blob
        const blob = await new Promise<Blob | null>((resolve) => {
          if (!resultCanvas) {
            resolve(null);
            return;
          }
          resultCanvas.toBlob((b) => resolve(b), "image/jpeg", 0.9);
        });

        if (!blob) {
          throw new Error("Error al generar la imagen recortada");
        }

        const originalName = file?.name.replace(/\.[^/.]+$/, "") || "document";
        const croppedFile = new File([blob], `${originalName}_cropped.jpg`, {
          type: "image/jpeg",
        });

        // Create preview URL and store the cropped file
        const previewDataUrl = URL.createObjectURL(blob);
        setPreviewUrl(previewDataUrl);
        setCroppedFileRef(croppedFile);
        setStep("preview");
      } catch (e) {
        if (import.meta.env.DEV) {
          console.error("Error cropping image:", e);
        }
        // Provide a user-friendly error message
        let errorMessage = "Error al recortar la imagen. ";
        if (e instanceof Error) {
          // Check for common OpenCV/perspective transform errors
          if (
            e.name === "TypeError" ||
            e.message.toLowerCase().includes("type")
          ) {
            errorMessage +=
              "El área seleccionada puede ser inválida. Intenta ajustar las esquinas o usa la imagen original.";
          } else {
            errorMessage += e.message;
          }
        } else {
          errorMessage +=
            "Intenta ajustar las esquinas o usa la imagen original.";
        }
        setError(errorMessage);
      } finally {
        // Clean up resources
        if (imageBitmap) {
          imageBitmap.close();
        }
        if (canvas) {
          canvas.width = 0;
          canvas.height = 0;
        }
        if (resultCanvas) {
          resultCanvas.width = 0;
          resultCanvas.height = 0;
        }
      }
    });
  };

  // Handle retry - go back to crop step
  const handleRetry = () => {
    // Clean up preview URL to prevent memory leaks
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setCroppedFileRef(null);
    setStep("crop");
    setError(null);
    setRetryCount((prev) => prev + 1);
  };

  // Handle final submission
  const handleSubmit = () => {
    if (!croppedFileRef) return;
    void runFinalizeAction(() => {
      onCropComplete(croppedFileRef);
      onOpenChange(false);
    });
  };

  // Handle using original image without cropping
  const handleUseOriginal = () => {
    if (!file) return;
    void runFinalizeAction(() => {
      onCropComplete(file);
      onOpenChange(false);
    });
  };

  const handleCancel = () => {
    if (isFinalizing) return;
    onCancel();
    onOpenChange(false);
  };

  const showUseOriginalOption =
    retryCount >= MAX_RETRY_BEFORE_ORIGINAL || Boolean(scannerError);

  const isActionDisabled =
    !corners ||
    (scannerLoading && !scannerError) ||
    isProcessing ||
    isLoadingFile ||
    isFinalizing;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "overflow-hidden flex flex-col",
          // Mobile: fullscreen. Overrides base Dialog classes via CSS source order.
          "top-0 left-0 translate-x-0 translate-y-0 w-screen h-[100dvh] max-w-none max-h-none rounded-none border-0 p-3 gap-2",
          // Desktop (>= 640px): restore centered modal with constrained size.
          "sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-full sm:max-w-[900px] sm:max-h-[90vh] sm:rounded-lg sm:border sm:p-6 sm:gap-4 sm:h-auto",
        )}
        showCloseButton={false}
      >
        <DialogHeader
          className={isMobile ? "pt-[env(safe-area-inset-top,0px)]" : undefined}
        >
          <DialogTitle className="flex items-center gap-2">
            {step === "crop" ? (
              <>
                <Scissors className="size-5" />
                {cropTitle}
              </>
            ) : (
              <>
                <Check className="size-5" />
                {previewTitle}
              </>
            )}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {step === "crop" ? cropDescription : previewDescription}
          </DialogDescription>
        </DialogHeader>

        <div
          className={cn(
            "flex-1 overflow-hidden min-h-0",
            isMobile ? "py-2" : "py-6",
          )}
        >
          {isLoadingFile && (
            <div className="flex flex-col items-center justify-center p-12">
              <Loader2 className="size-8 animate-spin text-primary" />
              <p className="mt-4 text-muted-foreground">Cargando archivo...</p>
            </div>
          )}

          {error && (
            <Alert
              variant="destructive"
              className="mb-4 bg-red-50 flex items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <AlertCircle className="size-4 shrink-0" />
                <AlertDescription>{error}</AlertDescription>
              </div>
              <Button
                variant="outline"
                size={isMobile ? "default" : "sm"}
                onClick={handleUseOriginal}
                disabled={isFinalizing}
                className={cn("shrink-0 ml-4", isMobile && "h-12 text-base")}
              >
                <ImageIcon className="size-4" />
                Usar original
              </Button>
            </Alert>
          )}

          {scannerError && step === "crop" && !error && (
            <Alert
              variant="default"
              className="mb-4 border-amber-500 bg-amber-50 text-amber-900"
            >
              <div
                className={cn(
                  "flex gap-3",
                  isMobile ? "flex-col" : "items-center justify-between",
                )}
              >
                <div className="flex min-w-0 items-start gap-2">
                  <AlertCircle className="mt-0.5 size-4 shrink-0 text-amber-600" />
                  <AlertDescription className="text-amber-900">
                    {scannerError}
                  </AlertDescription>
                </div>
                <div
                  className={cn(
                    "flex shrink-0 gap-2",
                    isMobile && "w-full flex-col",
                  )}
                >
                  <Button
                    variant="outline"
                    size={isMobile ? "default" : "sm"}
                    onClick={retryScanner}
                    disabled={isFinalizing}
                    className={cn(isMobile && "h-12 text-base")}
                  >
                    <RotateCcw className="size-4" />
                    Reintentar
                  </Button>
                  <Button
                    variant="outline"
                    size={isMobile ? "default" : "sm"}
                    onClick={handleUseOriginal}
                    disabled={isFinalizing}
                    className={cn(isMobile && "h-12 text-base")}
                  >
                    <ImageIcon className="size-4" />
                    Usar original
                  </Button>
                </div>
              </div>
            </Alert>
          )}

          {/* Warning for crossed corners */}
          {cornersWarning && step === "crop" && (
            <Alert
              variant="default"
              className="mb-4 border-amber-500 bg-amber-50"
            >
              <AlertCircle className="size-4 text-amber-600 shrink-0" />
              <AlertDescription className="text-amber-800">
                {cornersWarning}
              </AlertDescription>
            </Alert>
          )}

          {/* Step 1: Crop editor - shown even with errors so user can adjust corners */}
          {step === "crop" && imageSrc && !isLoadingFile && (
            <div className="flex flex-col justify-center h-full">
              <DocumentCropper
                ref={cropperRef}
                imageSrc={imageSrc}
                onCornersChange={handleCornersChange}
                onUseOriginal={handleUseOriginal}
              />
            </div>
          )}

          {/* Step 2: Preview */}
          {step === "preview" && previewUrl && (
            <div className="flex flex-col items-center justify-center h-full">
              <img
                src={previewUrl}
                alt="Vista previa del documento recortado"
                className={cn(
                  "max-w-full object-contain rounded-lg shadow-lg border border-gray-200",
                  isMobile
                    ? "max-h-[calc(100dvh-12rem)]"
                    : "max-h-[calc(90vh-16rem)]",
                )}
              />
            </div>
          )}
        </div>

        <DialogFooter
          className={cn(
            "flex-row justify-between sm:justify-between",
            isMobile && "gap-3 px-1",
          )}
        >
          {step === "crop" ? (
            <>
              <Button
                variant="ghost"
                onClick={handleCancel}
                disabled={isProcessing || isFinalizing}
                className={isMobile ? "h-12 text-base min-w-[44px]" : undefined}
              >
                Cancelar
              </Button>
              <Button
                variant="default"
                onClick={handleConfirmCrop}
                disabled={isActionDisabled}
                className={isMobile ? "h-12 text-base min-w-[44px]" : undefined}
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Procesando...
                  </>
                ) : scannerLoading && !scannerError ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Preparando recorte...
                  </>
                ) : (
                  <>
                    <Scissors className="size-4" />
                    {error ? "Reintentar recorte" : "Confirmar recorte"}
                  </>
                )}
              </Button>
            </>
          ) : (
            <>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleRetry}
                  disabled={isFinalizing}
                  className={isMobile ? "h-12 text-base" : undefined}
                >
                  <RotateCcw className="size-4" />
                  Reintentar
                </Button>
                {showUseOriginalOption && (
                  <Button
                    variant="ghost"
                    onClick={handleUseOriginal}
                    disabled={isFinalizing}
                    className={isMobile ? "h-12 text-base" : undefined}
                  >
                    <ImageIcon className="size-4" />
                    Usar original
                  </Button>
                )}
              </div>
              <Button
                variant="default"
                onClick={handleSubmit}
                disabled={isFinalizing}
                className={isMobile ? "h-12 text-base" : undefined}
              >
                {isFinalizing ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Send className="size-4" />
                )}
                {isFinalizing ? "Enviando..." : "Enviar"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
